import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../loaders/spinner/Spinner';
import { Window } from '../window/Window';
import styles from './AiStudioWindow.module.scss';

export const AiStudioWindow = ({
    aiStudioStartupAppId: appId, image, uploadFiles,
    telemetryCategory, onClose, pluginId
}: AiStudioWindowProps) => {

    const { i18n } = useTranslation();
    const containerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    let unmounted = false;

    const startAiStudio = () => {
        try {
            window.__aiStudio.launch({

                appId: appId ?? null,

                target: containerRef.current,
                locale: i18n.language,

                telemetry: telemetryCategory ? {
                    category: telemetryCategory,
                    area: pluginId + '-editor'
                } : {},

                photo: image && {
                    label: image.name,
                    url: image.url
                },

                on: {
                    launched: () => {
                        if (unmounted) {
                            onClose();
                            window.__aiStudio.destroy();
                        } else {
                            setLoading(false);
                        }
                    },

                    save: (results: {
                        appIds: string[];
                        blob: Blob;
                    }[]) => {
                        const files = results.map(result => {
                            const fileName = image ? image.name : "image";
                            return new File([result.blob], fileName, { type: result.blob?.type });
                        });
                        uploadFiles(files);
                    },

                    closed: () => {
                        onClose();
                        window.__aiStudio.destroy();
                    }
                }
            });
        } catch (error) {
            onClose();
            console.error(error);
        }
    };

    useEffect(() => {

        if (containerRef) {
            startAiStudio();
        }

        return () => {
            unmounted = true;
            if (window.__aiStudio.destroy) {
                window.__aiStudio.destroy();
            }
        };
    }, []);

    const closeWindow = () => {
        onClose();
        if (window.__aiStudio.destroy) {
            window.__aiStudio.destroy();
        }
    };

    return (
        <Window
            fullHeight
            fullWidth
            className={styles.windowAiStudio}
            close={closeWindow}>

            {loading && (
                <Spinner spinnerSize='large' overlaySize='full' color='white' />
            )}

            <div className={styles.windowAiStudioContent} ref={containerRef} />

        </Window>
    );
};

type AiStudioWindowProps = {

    aiStudioStartupAppId?: string;

    image?: {
        name: string;
        url: string;
    };
    uploadFiles: (files: File[]) => void;

    onClose: () => void;

    telemetryCategory?: string;
    pluginId: string;
};